import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Goal } from '../types';

interface GoalInput {
  name: string;
  emoji: string;
  target_amount: number;
  current_amount: number;
  deadline: string | null;
}

export function useGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGoals = useCallback(async () => {
    if (!user) {
      setGoals([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }

    setGoals((data as Goal[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchGoals();
  }, [fetchGoals]);

  const addGoal = async (input: GoalInput) => {
    if (!user) throw new Error('Not authenticated');

    const { error: insertError } = await supabase.from('goals').insert({
      user_id: user.id,
      name: input.name.trim(),
      emoji: input.emoji.trim() || '🎯',
      target_amount: input.target_amount,
      current_amount: input.current_amount,
      deadline: input.deadline,
    });

    if (insertError) throw insertError;
    await fetchGoals();
  };

  const updateGoal = async (id: string, input: GoalInput) => {
    if (!user) throw new Error('Not authenticated');

    const { error: updateError } = await supabase
      .from('goals')
      .update({
        name: input.name.trim(),
        emoji: input.emoji.trim() || '🎯',
        target_amount: input.target_amount,
        current_amount: input.current_amount,
        deadline: input.deadline,
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (updateError) throw updateError;
    await fetchGoals();
  };

  const deleteGoal = async (id: string) => {
    if (!user) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('goals')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (deleteError) throw deleteError;
    await fetchGoals();
  };

  return {
    goals,
    loading,
    error,
    addGoal,
    updateGoal,
    deleteGoal,
    refetch: fetchGoals,
  };
}
